/* LEGEND — floating TRADE JOURNAL card. Per-symbol round-trip history: each
   trade bundles every scale-in and scale-out, its avg in/out, hold time, and
   final realized P&L; the live open position rides on top as an in-progress
   trade. Ported from the prototype's legend/trades.jsx onto the pos-card
   chrome, drag/collapse modeled on PblCard (x:null = right-anchored). Data
   arrives as a prop — Terminal/ChartWindow own the getJournal hydrate +
   subscribeJournal re-hydrate tick. */
import React from 'react';
import { Icon } from './Widgets';
import { fmtPct, fmtPx4, fmtQty, fmtUsd, etClock } from '../lib/format';
import { tradeStats, fmtHold, fmtAgo } from '../lib/journal';
import type { JournalFill, JournalTrade } from '../lib/journal';

const { useState: useStateTj, useRef: useRefTj } = React;
const h = React.createElement;

function kpi(k: string, v: string, tone?: string) {
  return h('div', { className: 'tj-kpi', key: k },
    h('span', { className: 'tjk-v num' + (tone ? ' ' + tone : '') }, v),
    h('span', { className: 'tjk-k' }, k));
}

/* ---- the ins/outs ladder for a trade ---- */
function FillLadder({ fills }: { fills: JournalFill[] }) {
  return h('div', { className: 'tj-fills' },
    fills.map((f, i) => h('div', { className: 'tj-fill ' + f.t, key: i },
      h('span', { className: 'tf-dot' }),
      h('span', { className: 'tf-tag' }, f.tag),
      h('span', { className: 'tf-qty mono' }, fmtQty(f.qty)),
      h('span', { className: 'tf-px num' }, '$' + fmtPx4(f.px)),
      h('span', { className: 'tf-time mono' }, etClock(f.time)))));
}

/* ---- live, in-progress trade ---- */
function OpenTrade({ tr, lastPrice }: { tr: JournalTrade; lastPrice: number }) {
  const s = tradeStats(tr, lastPrice);
  const up = s.unreal >= 0;
  return h('div', { className: 'tj-open' },
    h('div', { className: 'tjo-head' },
      h('span', { className: 'tjo-live' }, h('i', null), 'Open'),
      h('span', { className: 'tjo-pnl num ' + (up ? 'up' : 'down') }, fmtUsd(s.unreal)),
      h('span', { className: 'tjo-pct num ' + (up ? 'up' : 'down') }, fmtPct(s.unreal / (s.avgIn * (s.openQty || 1))))),
    h('div', { className: 'tjo-avgs mono' },
      h('span', null, fmtQty(s.openQty) + ' sh'),
      h('span', null, 'avg $' + fmtPx4(s.avgIn)),
      h('span', null, 'last $' + fmtPx4(lastPrice)),
      h('span', null, fmtHold(s.hold))),
    h(FillLadder, { fills: tr.fills }));
}

/* ---- one closed round-trip, expandable to its fills ---- */
function ClosedTrade({ tr, open, onToggle }: { tr: JournalTrade; open: boolean; onToggle: () => void }) {
  const s = tradeStats(tr);
  const win = s.realized >= 0;
  return h('div', { className: 'tj-trade' + (open ? ' open' : '') + (win ? ' win' : ' loss') },
    h('button', { className: 'tjt-row', onClick: onToggle },
      h('span', { className: 'tjt-accent' }),
      h('span', { className: 'tjt-when' }, fmtAgo(tr.closeTime)),
      h('span', { className: 'tjt-flow mono' }, s.ins.length + '↓ ' + s.outs.length + '↑'),
      h('span', { className: 'tjt-spacer' }),
      h('span', { className: 'tjt-pct num ' + (win ? 'up' : 'down') }, fmtPct(s.realizedPct)),
      h('span', { className: 'tjt-pnl num ' + (win ? 'up' : 'down') }, fmtUsd(s.realized)),
      h('span', { className: 'tjt-caret' + (open ? ' open' : '') }, Icon.chev())),
    open ? h('div', { className: 'tjt-detail' },
      h('div', { className: 'tjt-avgs mono' },
        h('span', null, 'avg in ', h('b', null, '$' + fmtPx4(s.avgIn))),
        h('span', null, 'avg out ', h('b', null, '$' + fmtPx4(s.avgOut))),
        h('span', null, fmtQty(s.inSh) + ' sh'),
        h('span', null, fmtHold(s.hold))),
      h(FillLadder, { fills: tr.fills })) : null);
}

export function JournalCard(props: any) {
  const { book, symbol, lastPrice, pos, collapsed, docked, onMove, onToggle, onClose } = props;
  const open: JournalTrade | null = (book && book.open) || null;
  const closed: JournalTrade[] = (book && book.closed) || [];
  const [expanded, setExpanded] = useStateTj<Record<string, boolean>>({});
  const cardRef = useRefTj<any>(null);

  function startDrag(e: any) {
    if (e.target.closest('button')) return;
    e.preventDefault();
    const ox = pos.x == null && cardRef.current ? cardRef.current.offsetLeft : pos.x || 0;
    const sx = e.clientX, sy = e.clientY, oy = pos.y;
    const move = (ev: any) => onMove({ x: ox + (ev.clientX - sx), y: oy + (ev.clientY - sy) });
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  // session summary across closed trades. Breakeven counts as a win — the same
  // >=0 convention the row accents and the +$ signing already use.
  const stats = closed.map((tr) => tradeStats(tr));
  const net = stats.reduce((a, s) => a + s.realized, 0);
  const wins = stats.filter((s) => s.realized >= 0).length;
  const winRate = stats.length ? Math.round((wins / stats.length) * 100) : 0;

  const cardStyle = docked
    ? undefined
    : pos.x == null
      ? { right: '14px', top: pos.y + 'px' }
      : { left: pos.x + 'px', top: pos.y + 'px' };

  return h('div', {
    className: 'pos-card trade-card' + (collapsed ? ' collapsed' : '') + (docked ? ' docked' : ''),
    style: cardStyle, ref: cardRef,
  },
    h('div', { className: 'pc-head', onPointerDown: docked ? undefined : startDrag },
      h('span', { className: 'pc-sym' }, symbol),
      h('span', { className: 'tj-tag' }, 'Trade journal'),
      h('div', { className: 'pc-actions' },
        h('button', { className: 'icon-btn sm', onClick: onToggle, title: collapsed ? 'Expand' : 'Collapse' }, Icon.chev()),
        h('button', { className: 'icon-btn sm', onClick: onClose, title: 'Close card' }, Icon.x()))),

    h('div', { className: 'pc-body tj-body' },
      h('div', { className: 'tj-summary' },
        kpi('Net realized', fmtUsd(net), net >= 0 ? 'up' : 'down'),
        kpi('Win rate', winRate + '%'),
        kpi('Trades', String(closed.length))),

      open ? h(OpenTrade, { tr: open, lastPrice }) : null,

      closed.length ? h('div', { className: 'tj-list' },
        closed.map((tr) => h(ClosedTrade, {
          key: tr.id, tr, open: !!expanded[tr.id],
          onToggle: () => setExpanded((e) => Object.assign({}, e, { [tr.id]: !e[tr.id] })),
        }))) : null,

      !open && !closed.length ? h('div', { className: 'pc-empty' },
        h('div', { className: 'pe-ic' }, Icon.bolt()),
        h('div', { className: 'pe-t' }, 'No trades yet'),
        h('div', { className: 'pe-s' }, symbol + ' has no round-trips on record. Fills land here as you scale in and out.')) : null));
}
